// src/hooks/usePDFAnnotations.ts
import { useState, useCallback } from 'react';
import { message } from 'antd';
import { auth } from '../utils/firebase';
import type {
  Annotation,
  CreateAnnotationRequest,
  UpdateAnnotationRequest,
} from '../types/annotation';

interface UsePDFAnnotationsReturn {
  annotations: Annotation[];
  loading: boolean;
  error: string | null;
  fetchAnnotations: (documentId: string) => Promise<void>;
  createAnnotation: (data: CreateAnnotationRequest) => Promise<Annotation | null>;
  updateAnnotation: (id: string, data: UpdateAnnotationRequest) => Promise<Annotation | null>;
  deleteAnnotation: (id: string) => Promise<boolean>;
  refreshAnnotations: () => Promise<void>;
}

export const usePDFAnnotations = (): UsePDFAnnotationsReturn => {
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentDocumentId, setCurrentDocumentId] = useState<string | null>(null);
  // Track if initial fetch has completed - used to prevent remounting PDFViewer during operations
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  /**
   * Fetch annotations for a specific document
   */
  const fetchAnnotations = useCallback(async (documentId: string) => {
    setLoading(true);
    setError(null);
    setCurrentDocumentId(documentId);
    setHasInitialLoad(false); // Reset for new document

    try {
      const authToken = await auth.currentUser?.getIdToken();

      if (!authToken) {
        throw new Error('Authentication required');
      }

      console.log(`[usePDFAnnotations] Fetching annotations for document: ${documentId}`);

      const response = await fetch(`${API_URL}/annotations/${documentId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch annotations');
      }

      const data = await response.json();

      console.log('[usePDFAnnotations] Fetch response:', data);

      // Extract annotations - handle possible response structures
      let fetchedAnnotations: Annotation[] = [];

      if (data.data?.annotations) {
        fetchedAnnotations = data.data.annotations;
      } else if (data.annotations) {
        fetchedAnnotations = data.annotations;
      } else if (Array.isArray(data.data)) {
        fetchedAnnotations = data.data;
      } else if (Array.isArray(data)) {
        fetchedAnnotations = data;
      }

      // Transform snake_case fields to camelCase
      const transformedAnnotations = fetchedAnnotations.map((ann: any) => ({
        id: ann.id || ann.annotation_id,
        documentId: ann.documentId || ann.document_id,
        userId: ann.userId || ann.user_id,
        page: ann.page,
        type: ann.type,
        position: ann.position,
        comment: ann.comment || '',
        tags: ann.tags || [],
        color: ann.color,
        authorName: ann.authorName || ann.author_name || '',
        authorEmail: ann.authorEmail || ann.author_email || '',
        createdAt: ann.createdAt || ann.created_at,
        updatedAt: ann.updatedAt || ann.updated_at,
      }));

      console.log(`[usePDFAnnotations] Loaded ${transformedAnnotations.length} annotations`);

      setAnnotations(transformedAnnotations);
      setLoading(false);
      setHasInitialLoad(true); // Mark initial load as complete
    } catch (err: any) {
      console.error('[usePDFAnnotations] Error fetching annotations:', err);
      setError(err.message || 'Failed to fetch annotations');
      setAnnotations([]);
      setLoading(false);
      message.error(err.message || 'Failed to load annotations');
    }
  }, [API_URL]);

  /**
   * Create a new annotation
   */
  const createAnnotation = useCallback(async (
    data: CreateAnnotationRequest
  ): Promise<Annotation | null> => {
    // Don't set loading to true here - it would cause PDFViewer to unmount and remount
    setError(null);

    try {
      const authToken = await auth.currentUser?.getIdToken();

      if (!authToken) {
        throw new Error('Authentication required');
      }

      console.log('[usePDFAnnotations] Creating annotation:', data);

      // Get current user info for author tracking
      const currentUser = auth.currentUser;
      console.log('[usePDFAnnotations] currentUser object:', currentUser);
      console.log('[usePDFAnnotations] currentUser email:', currentUser?.email);
      console.log('[usePDFAnnotations] currentUser displayName:', currentUser?.displayName);

      // Use displayName if available, otherwise fall back to email
      const authorName = data.authorName || currentUser?.displayName || currentUser?.email || '';
      const authorEmail = data.authorEmail || currentUser?.email || '';
      console.log('[usePDFAnnotations] Final author info being sent:', { authorName, authorEmail });

      // Transform camelCase to snake_case for backend
      const requestBody = {
        document_id: data.documentId,
        page: data.page,
        type: data.type,
        position: data.position,
        comment: data.comment || '',
        tags: data.tags || [],
        color: data.color || '#FFEB3B',
        author_name: authorName,
        author_email: authorEmail,
      };

      console.log('[usePDFAnnotations] Full request body:', requestBody);

      const response = await fetch(`${API_URL}/annotations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create annotation');
      }

      const responseData = await response.json();

      console.log('[usePDFAnnotations] Create response:', responseData);

      // Extract annotation from response - handle multiple structures
      let createdAnnotation: any;

      if (responseData.data?.annotation) {
        createdAnnotation = responseData.data.annotation;
        console.log('[usePDFAnnotations] Found annotation at data.annotation');
      } else if (responseData.annotation) {
        createdAnnotation = responseData.annotation;
        console.log('[usePDFAnnotations] Found annotation at annotation');
      } else if (responseData.data?.data?.annotation) {
        createdAnnotation = responseData.data.data.annotation;
        console.log('[usePDFAnnotations] Found annotation at data.data.annotation');
      } else {
        throw new Error('Invalid response format');
      }

      console.log('[usePDFAnnotations] createdAnnotation object:', createdAnnotation);
      console.log('[usePDFAnnotations] createdAnnotation.authorName:', createdAnnotation.authorName);
      console.log('[usePDFAnnotations] createdAnnotation.authorEmail:', createdAnnotation.authorEmail);

      // Transform snake_case to camelCase
      const transformedAnnotation: Annotation = {
        id: createdAnnotation.id || createdAnnotation.annotation_id,
        documentId: createdAnnotation.documentId || createdAnnotation.document_id,
        userId: createdAnnotation.userId || createdAnnotation.user_id,
        page: createdAnnotation.page,
        type: createdAnnotation.type,
        position: createdAnnotation.position,
        comment: createdAnnotation.comment || '',
        tags: createdAnnotation.tags || [],
        color: createdAnnotation.color,
        authorName: createdAnnotation.authorName || createdAnnotation.author_name || '',
        authorEmail: createdAnnotation.authorEmail || createdAnnotation.author_email || '',
        createdAt: createdAnnotation.createdAt || createdAnnotation.created_at,
        updatedAt: createdAnnotation.updatedAt || createdAnnotation.updated_at,
      };

      console.log('[usePDFAnnotations] transformedAnnotation:', transformedAnnotation);

      // Add to local state
      setAnnotations((prev) => [...prev, transformedAnnotation]);

      message.success('Comment created');

      return transformedAnnotation;
    } catch (err: any) {
      console.error('[usePDFAnnotations] Error creating annotation:', err);
      setError(err.message || 'Failed to create annotation');
      message.error(err.message || 'Failed to create annotation');
      return null;
    }
  }, [API_URL]);

  /**
   * Update an existing annotation
   */
  const updateAnnotation = useCallback(async (
    id: string,
    data: UpdateAnnotationRequest
  ): Promise<Annotation | null> => {
    // Don't set loading to true here - it would cause PDFViewer to unmount and remount
    setError(null);

    try {
      const authToken = await auth.currentUser?.getIdToken();

      if (!authToken) {
        throw new Error('Authentication required');
      }

      console.log(`[usePDFAnnotations] Updating annotation ${id}:`, data);

      const response = await fetch(`${API_URL}/annotations/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update annotation');
      }

      const responseData = await response.json();

      console.log('[usePDFAnnotations] Update response:', responseData);

      // Extract annotation from response
      let updatedAnnotation: any;

      if (responseData.data?.annotation) {
        updatedAnnotation = responseData.data.annotation;
      } else if (responseData.annotation) {
        updatedAnnotation = responseData.annotation;
      } else if (responseData.data?.data?.annotation) {
        updatedAnnotation = responseData.data.data.annotation;
      } else {
        throw new Error('Invalid response format');
      }

      // Transform snake_case to camelCase
      const transformedAnnotation: Annotation = {
        id: updatedAnnotation.id || updatedAnnotation.annotation_id,
        documentId: updatedAnnotation.documentId || updatedAnnotation.document_id,
        userId: updatedAnnotation.userId || updatedAnnotation.user_id,
        page: updatedAnnotation.page,
        type: updatedAnnotation.type,
        position: updatedAnnotation.position,
        comment: updatedAnnotation.comment || '',
        tags: updatedAnnotation.tags || [],
        color: updatedAnnotation.color,
        authorName: updatedAnnotation.authorName || updatedAnnotation.author_name || '',
        authorEmail: updatedAnnotation.authorEmail || updatedAnnotation.author_email || '',
        createdAt: updatedAnnotation.createdAt || updatedAnnotation.created_at,
        updatedAt: updatedAnnotation.updatedAt || updatedAnnotation.updated_at,
      };

      // Update local state - preserve author info from the existing annotation
      setAnnotations((prev) =>
        prev.map((ann) => (ann.id === id ? { ...transformedAnnotation, authorName: ann.authorName, authorEmail: ann.authorEmail } : ann))
      );

      message.success('Annotation updated successfully');

      return transformedAnnotation;
    } catch (err: any) {
      console.error('[usePDFAnnotations] Error updating annotation:', err);
      setError(err.message || 'Failed to update annotation');
      message.error(err.message || 'Failed to update annotation');
      return null;
    }
  }, [API_URL]);

  /**
   * Delete an annotation
   */
  const deleteAnnotation = useCallback(async (id: string): Promise<boolean> => {
    // Don't set loading to true here - it would cause PDFViewer to unmount and remount
    setError(null);

    try {
      const authToken = await auth.currentUser?.getIdToken();

      if (!authToken) {
        throw new Error('Authentication required');
      }

      console.log(`[usePDFAnnotations] Deleting annotation ${id}`);

      const response = await fetch(`${API_URL}/annotations/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete annotation');
      }

      console.log(`[usePDFAnnotations] Annotation ${id} deleted successfully`);

      // Remove from local state
      setAnnotations((prev) => prev.filter((ann) => ann.id !== id));

      message.success('Comment deleted');

      return true;
    } catch (err: any) {
      console.error('[usePDFAnnotations] Error deleting annotation:', err);
      setError(err.message || 'Failed to delete annotation');
      message.error(err.message || 'Failed to delete annotation');
      return false;
    }
  }, [API_URL]);

  /**
   * Refresh annotations for the current document
   */
  const refreshAnnotations = useCallback(async () => {
    if (currentDocumentId) {
      await fetchAnnotations(currentDocumentId);
    }
  }, [currentDocumentId, fetchAnnotations]);

  return {
    annotations,
    loading,
    error,
    fetchAnnotations,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation,
    refreshAnnotations,
  };
};
